// src/features/Planning/components/StepApproximateBudget.jsx
import { useState } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  VStack,
  Input,
  Select,
} from "@chakra-ui/react";

export default function StepApproximateBudget({
  formData,
  setFormData,
  onNext,
  onBack,
  isLastStep,
  onSubmit,
  viewMode,
}) {
  const budget = formData?.approximateBudget ?? "";
  const unit = formData?.budgetUnit ?? "crore";

  // NEW — tracks whether submit/next has already been triggered
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = async () => {
    if (isSubmitting) return; // guard against double click
    setIsSubmitting(true);
    try {
      if (isLastStep) {
        await onSubmit();
      } else {
        await onNext();
      }
    } finally {
      // Only reset if it's NOT the last step (after final submit, page navigates away anyway)
      if (!isLastStep) setIsSubmitting(false);
    }
  };

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      {/* Content Area */}
      <Box pointerEvents={viewMode ? "none" : "auto"}>
        <VStack spacing={4} align="stretch">
          <Text fontSize="17px" fontWeight="600" color="gray.800" textAlign="center">
            Approximate Project Budget
          </Text>

          <Box bg="white" borderRadius="12px" border="1px solid" borderColor="gray.200" p={5}>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="600" fontSize="14px" color="gray.700">
                Enter Your Budget
              </Text>

              <Flex align="center" gap={3}>
                <Box flex="1">
                  <Input
                    value={budget}
                    onChange={(e) => handleChange("approximateBudget", e.target.value)}
                    placeholder="0"
                    fontSize="32px"
                    fontWeight="600"
                    border="none"
                    borderBottom="2px solid"
                    borderBottomColor="gray.300"
                    borderRadius={0}
                    type="number"
                    min="0"
                    textAlign="left"
                    px={0}
                    color="gray.800"
                    isDisabled={isSubmitting}
                    _focus={{ borderBottomColor: "cyan.500", boxShadow: "none" }}
                    _hover={{ borderBottomColor: "cyan.400" }}
                  />
                </Box>
                <Select
                  value={unit}
                  onChange={(e) => handleChange("budgetUnit", e.target.value)}
                  fontWeight="600"
                  fontSize="15px"
                  color="white"
                  bg="cyan.500"
                  borderRadius="8px"
                  w="120px"
                  h="44px"
                  border="none"
                  isDisabled={isSubmitting}
                  _focus={{ bg: "cyan.600", boxShadow: "none" }}
                  _hover={{ bg: "cyan.600" }}
                >
                  <option value="thousand" style={{ color: '#000' }}>Thousand</option>
                  <option value="lakh" style={{ color: '#000' }}>Lakh</option>
                  <option value="crore" style={{ color: '#000' }}>Crore</option>
                </Select>
              </Flex>

              <Text fontSize="13px" color="gray.500" mt={2}>
                This helps us understand your project requirements better
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Fixed Navigation Buttons */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          isDisabled={isSubmitting}
          _hover={{ bg: "cyan.50", borderColor: "cyan.600" }}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleNext}
          variant="solid"
          bg="cyan.500"
          color="white"
          border="none"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          isLoading={isSubmitting}
          loadingText={isLastStep ? "Submitting..." : "Saving..."}
          isDisabled={isSubmitting || (isLastStep && viewMode)}
          _hover={{ bg: "cyan.600" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}